import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

const GET_DEAL_PUBLIC_DATA_QUERY = gql(`
  query GetDealPublicData($id: ID!) {
    deal(id: $id) {
      id
      showInDealDigest
      publicData {
        name
        description
        leads
        round
        founders
        dealInviteLink
        showInBaskets
      }
    }
  }
`);

const UPDATE_SHOW_IN_DEAL_DIGEST_MUTATION = gql(`
  mutation SetShowInDealDigest($id: ID!, $showInDealDigest: Boolean!) {
    setShowInDealDigest(id: $id, showInDealDigest: $showInDealDigest) {
      id
      showInDealDigest
    }
  }
`);

const UPDATE_PUBLIC_DATA_MUTATION = gql(`
  mutation UpsertDealPublicData(
    $id: ID!
    $name: String
    $description: String
    $leads: String
    $round: String
    $founders: String
    $dealInviteLink: String
    $showInBaskets: Boolean
  ) {
    upsertDealPublicData(
      id: $id
      name: $name
      description: $description
      leads: $leads
      round: $round
      founders: $founders
      dealInviteLink: $dealInviteLink
      showInBaskets: $showInBaskets
    ) {
      id
      publicData {
        name
        description
        leads
        round
        founders
        dealInviteLink
        showInBaskets
      }
    }
  }
`);

const DealPublicInformation: FC = () => {
  const { dealId } = useParams<{ dealId: string }>() as { dealId: string };

  const { loading, error, data, refetch } = useQuery(GET_DEAL_PUBLIC_DATA_QUERY, {
    variables: {
      id: dealId,
    },
  });

  const [
    updateShowInDealDigest,
    { loading: updateShowInDealDigestLoading, error: updateShowInDealDigestError },
  ] = useMutation(UPDATE_SHOW_IN_DEAL_DIGEST_MUTATION);

  const [updatePublicData, { loading: updatePublicDataLoading, error: updatePublicDataError }] =
    useMutation(UPDATE_PUBLIC_DATA_MUTATION);

  if (loading) return <LoadingIndicator />;

  if (error || !data || !dealId) return <ErrorMessage error={error} refetch={refetch} />;

  return (
    <>
      <FormPanelWithReadMode
        loading={updateShowInDealDigestLoading}
        error={updateShowInDealDigestError}
        onSubmit={data => {
          updateShowInDealDigest({
            variables: {
              id: dealId,
              showInDealDigest: data.showInDealDigest,
            },
          });
        }}
      >
        <FormInput
          type="switch"
          fieldName="showInDealDigest"
          label="Show deal publicly"
          helperText="Enable this to show this deal publicly"
          defaultValue={data.deal.showInDealDigest}
        />
      </FormPanelWithReadMode>

      <FormPanelWithReadMode
        loading={updatePublicDataLoading}
        error={updatePublicDataError}
        onSubmit={data => {
          updatePublicData({
            variables: {
              id: dealId,
              name: data.name,
              description: data.description,
              leads: data.leads,
              round: data.round,
              founders: data.founders,
              dealInviteLink: data.dealInviteLink,
              showInBaskets: data.showInBaskets,
            },
          });
        }}
      >
        <FormInput
          type="text"
          fieldName="name"
          label="Name"
          helperText="Public name of the deal"
          defaultValue={data.deal.publicData?.name || ''}
        />
        <FormInput
          type="text"
          fieldName="description"
          label="Description"
          helperText="Public description of the deal"
          defaultValue={data.deal.publicData?.description || ''}
        />
        <FormInput
          type="text"
          fieldName="leads"
          label="Leads"
          helperText="Lead investors in the deal"
          defaultValue={data.deal.publicData?.leads || ''}
        />
        <FormInput
          type="text"
          fieldName="round"
          label="Round"
          helperText="Funding round information"
          defaultValue={data.deal.publicData?.round || ''}
        />
        <FormInput
          type="text"
          fieldName="founders"
          label="Founders"
          helperText="Company founders"
          defaultValue={data.deal.publicData?.founders || ''}
        />
        <FormInput
          type="text"
          fieldName="dealInviteLink"
          label="Deal invite link"
          helperText="Link to the deal invite"
          defaultValue={data.deal.publicData?.dealInviteLink || ''}
        />
        <FormInput
          type="switch"
          fieldName="showInBaskets"
          label="Show in baskets"
          helperText="Enable this to show this deal in baskets"
          defaultValue={data.deal.publicData?.showInBaskets || false}
        />
      </FormPanelWithReadMode>
    </>
  );
};
export default DealPublicInformation;
